import React, { PropsWithChildren, ReactElement } from "react";
import { Box, BoxProps, Flex, HStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { ITopTradersReactions } from "src/api/server/news.types";
import { Section } from "src/components/base";
import { AggregatedReactionsBar } from "src/components/common/aggregated-reactions-bar/aggregated-reactions-bar";
import { useNavHeight } from "src/components/layout/hooks/use-nav-height";
import { useNewsUserReactions } from "src/components/pages/news/hooks/use-news-user-reactions";
import { TUserReactions } from "src/components/user-reactions/types";
import { UserReactions } from "src/components/user-reactions/user-reactions";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { ITooltip } from "src/types/common";
import { useRouterCaller } from "src/utils/router/use-router-caller";
import { TopTraderMarketReactionPreview } from "./components/top-trader-market-reaction-preview/top-trader-market-reaction-preview";
import { isReactionTimeWindowOpen } from "./utils/is-reaction-time-window-open";

interface IReactionSectionProps extends BoxProps {
    topNumberOfReactions: number;
    reactions: TUserReactions;
    learnMoreAboutMarketReactionsTooltip: ITooltip;
    topTradersReactions: ITopTradersReactions | null;
    AllTradersReactions: ReactElement;
    articleCreationDate: string;
}

export const ReactionSection = ({
    reactions,
    topNumberOfReactions,
    learnMoreAboutMarketReactionsTooltip,
    AllTradersReactions,
    topTradersReactions,
    articleCreationDate,
    ...props
}: PropsWithChildren<IReactionSectionProps>) => {
    const { t } = useTranslation("news");
    const { handleOnReactionChange, userReactions, highestReactionValue, allReactionsCount, aggregatedReactions } = useNewsUserReactions(reactions);
    const { NAV_HEIGHT_VARIATION } = useNavHeight();
    const { isCallerContentApp } = useRouterCaller();

    const isReactionsDisabled = isReactionTimeWindowOpen({ creationDate: new Date(articleCreationDate) });
    return (
        <Section scrollMarginTop={isCallerContentApp ? 0 : NAV_HEIGHT_VARIATION.map(m => m + 1)} {...props}>
            <HStack gap={0} justifyContent="space-between" alignItems="baseline" flexWrap="wrap">
                <Flex alignItems="baseline" w="100%">
                    <WfHeading component="h2" fontSize="2xl">
                        {allReactionsCount > 0 ? t("headline.reactions", { count: allReactionsCount }) : t("headline.reactions-plain")}
                    </WfHeading>
                    <WfPopover
                        content={learnMoreAboutMarketReactionsTooltip.content}
                        link={learnMoreAboutMarketReactionsTooltip.link}
                        PopoverTrigger={<PopoverButtonIconInfo sx={{ ml: 1 }} ariaLabel={learnMoreAboutMarketReactionsTooltip.content} />}
                    />
                </Flex>

                {allReactionsCount > 0 && (
                    <Box width="100%" maxWidth="100%" mt={2}>
                        <AggregatedReactionsBar reactions={aggregatedReactions} topNumberOfReactions={topNumberOfReactions} />
                    </Box>
                )}

                {topTradersReactions?.topTraderMarketReactions ? (
                    <TopTraderMarketReactionPreview
                        pt={[2, 3]}
                        topTraderMarketReactions={topTradersReactions.topTraderMarketReactions}
                        totalResults={topTradersReactions.totalResults}
                    />
                ) : null}

                {AllTradersReactions}
                <UserReactions
                    reactions={userReactions}
                    highestReactionValue={highestReactionValue}
                    onReactionChange={handleOnReactionChange}
                    allReactionsCount={allReactionsCount}
                    articleCreationDate={articleCreationDate}
                    isReactionsDisabled={isReactionsDisabled}
                />
            </HStack>
        </Section>
    );
};
